import { useSelector } from "react-redux";
import { selectTakes } from "../../../app/slices/TrainingSlice";
import {
  selectErrors,
  selectWarnings,
  ValidationError,
} from "../../../app/slices/ValidationSlice";
import { useCallback } from "react";
import Take from "../../../models/Take";
import Interaction from "../../../models/interactions/Interaction";
import LogData from "./LogData";
import Take3D, {
  getTake3DSequence,
  isTake3DTransitionEnabled,
} from "../../../models/Take3D";

export default function useErrorLogColumn() {
  const allTakes = useSelector(selectTakes);
  const allWarnings = useSelector(selectWarnings);
  const allErrors = useSelector(selectErrors);

  const getLogData = useCallback(() => {
    return allTakes
      .map((take) => {
        return getLogDataForTake(take);
      })
      .flat();
  }, [allTakes, allWarnings, allErrors]);

  const getLogDataForTake = (take: Take) => {
    // Disabled interactions are excluded from testing, so their issues are
    // hidden from the log — like commented-out code.
    return [
      ...getTakeOwnedInteractions(take),
      ...take.interactions.filter(
        (interaction) => interaction.disabled !== true
      ),
    ]
      .map((interaction) => {
        return getLogDataForInteraction(take, interaction);
      })
      .flat();
  };

  const getLogDataForInteraction = (parent: Take, interaction: Interaction) => {
    const logs: LogData[] = [];
    const isTakeSequence = getTake3DSequence(
      parent as Partial<Take3D> as Pick<Take3D, "sequence">
    )?.id === interaction.id;

    // errors
    allErrors[interaction.id]?.map((error: ValidationError | string) => {
      const message = typeof error === 'string' ? error : error.message;
      logs.push({
        interactionId: interaction.id,
        interactionName: interaction.name,
        takeId: isTakeSequence ? parent.id : undefined,
        opensTakeSequence: isTakeSequence,
        logLevel: "error",
        message: message,
        parentName: parent.name,
      });
    });

    // warnings
    allWarnings[interaction.id]?.map((warning) => {
      logs.push({
        interactionId: interaction.id,
        interactionName: interaction.name,
        takeId: isTakeSequence ? parent.id : undefined,
        opensTakeSequence: isTakeSequence,
        logLevel: "warning",
        message: warning,
        parentName: parent.name,
      });
    });

    return logs;
  };

  return { getLogData } as const;
}

const getTakeOwnedSequences = (take: Take): Interaction[] => {
  const take3D = take as Partial<Take3D>;
  const sequence = getTake3DSequence(
    take3D as Pick<Take3D, "sequence">
  );
  return sequence ? [sequence] : [];
};

const getTakeOwnedInteractions = (take: Take): Interaction[] => {
  const take3D = take as Partial<Take3D>;
  const interactions: Interaction[] = [];

  if (take3D.mainCamera) interactions.push(take3D.mainCamera);
  if (
    take3D.transition &&
    isTake3DTransitionEnabled(take3D as Pick<Take3D, "properties">)
  ) {
    interactions.push(take3D.transition);
  }

  return [...interactions, ...getTakeOwnedSequences(take)];
};
